// src/context/SidebarContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
    // Get authentication state
    const { user } = useContext(AuthContext) || {};
    const isAuthenticated = !!user;

    // Left sidebar states
    const [leftCollapsed, setLeftCollapsed] = useState(false);
    const [leftWidth, setLeftWidth] = useState(250);
    const [leftHidden, setLeftHidden] = useState(false);
    const [isLeftResizing, setIsLeftResizing] = useState(false);

    // Right sidebar states - rightVisible starts as undefined to allow auto-show logic
    const [rightVisible, setRightVisible] = useState(true);
    const [rightWidth, setRightWidth] = useState(300);
    const [isRightResizing, setIsRightResizing] = useState(false);

    // Shared states
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [contentWidth, setContentWidth] = useState(window.innerWidth);

    // Ref to track resize timer across renders
    const resizeTimerRef = useRef(null);
    
    // Function to apply logistics page defaults
    const applyLogisticsDefaults = useCallback(() => {
        const mobile = window.innerWidth <= 768;
        let logisticsPrefs;
        try {
            logisticsPrefs = localStorage.getItem('logisticsSidebarPreferences');
        } catch (error) {
            console.warn('Failed to read logistics preferences:', error);
        }
        if (logisticsPrefs && !mobile) {
            try {
                const prefs = JSON.parse(logisticsPrefs);
                setLeftCollapsed(prefs.leftCollapsed !== undefined ? prefs.leftCollapsed : true);
                setRightVisible(prefs.rightVisible !== undefined ? prefs.rightVisible : false);
                setLeftWidth(prefs.leftWidth || 250);
                setRightWidth(prefs.rightWidth || 300);
            } catch (error) {
                console.warn('Failed to parse logistics preferences:', error);
            }
        } else {
            // On mobile, always start with sidebars hidden
            setLeftCollapsed(true);
            setRightVisible(mobile ? false : false);
        }
    }, []);
    
    // Function to apply regular page defaults
    const applyRegularDefaults = useCallback(() => {
        const mobile = window.innerWidth <= 768;
        let regularPrefs;
        try {
            regularPrefs = localStorage.getItem('sidebarPreferences');
        } catch (error) {
            console.warn('Failed to read regular preferences:', error);
        }
        if (regularPrefs && !mobile) {
            try {
                const prefs = JSON.parse(regularPrefs);
                setLeftCollapsed(prefs.leftCollapsed || false);
                setRightVisible(prefs.rightVisible !== undefined ? prefs.rightVisible : true);
                setLeftWidth(prefs.leftWidth || 250);
                setRightWidth(prefs.rightWidth || 300);
            } catch (error) {
                console.warn('Failed to parse regular preferences:', error);
            }
        } else {
            // On mobile, always start with sidebars hidden
            // On desktop, start with left sidebar visible, right sidebar visible
            setLeftCollapsed(mobile ? true : false);
            setRightVisible(mobile ? false : true);
            setLeftWidth(250);
            setRightWidth(300);
        }
    }, []);

    // Load initial preferences
    useEffect(() => {
        applyRegularDefaults(); // Start with regular defaults

        const handleResize = () => {
            // CRITICAL: Clear existing timer using ref to prevent accumulation
            if (resizeTimerRef.current !== null) {
                clearTimeout(resizeTimerRef.current);
                resizeTimerRef.current = null;
            }

            resizeTimerRef.current = setTimeout(() => {
                resizeTimerRef.current = null;
                const width = window.innerWidth;
                const mobile = width <= 768;

                setContentWidth(width);

                // Auto-hide sidebars on mobile when transitioning from desktop to mobile
                setIsMobile(prev => {
                    if (mobile !== prev) {
                        if (mobile) {
                            setLeftCollapsed(true);
                            setRightVisible(false);
                        }
                    }
                    return mobile;
                });
            }, 100); // Throttle resize events
        };

        // Use both resize and orientationchange for better mobile detection
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        handleResize(); // Call immediately

        return () => {
            if (resizeTimerRef.current !== null) {
                clearTimeout(resizeTimerRef.current);
                resizeTimerRef.current = null;
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [applyRegularDefaults]);

    // Function to save current preferences based on page type
    const savePreferences = useCallback((isLogistics = false) => {
        // Use async localStorage to prevent blocking
        setTimeout(() => {
            try {
                const prefs = {
                    leftCollapsed,
                    leftWidth,
                    rightVisible,
                    rightWidth
                };
                const storageKey = isLogistics ? 'logisticsSidebarPreferences' : 'sidebarPreferences';
                localStorage.setItem(storageKey, JSON.stringify(prefs));
            } catch (error) {
                console.warn('Failed to save sidebar preferences:', error);
            }
        }, 0);
    }, [leftCollapsed, leftWidth, rightVisible, rightWidth]);

    // Calculate main content width
    const getContentMargins = () => {
        // No margins when not authenticated (no sidebars shown)
        if (!isAuthenticated) {
            return { left: 0, right: 0 };
        }

        if (isMobile) {
            return { left: 0, right: 0 };
        }

        const left = leftHidden ? 0 : (leftCollapsed ? 80 : leftWidth);
        const right = rightVisible ? rightWidth : 0;

        return { left, right };
    };

    // Toggle functions
    const toggleLeftSidebar = (forceClose = false) => {
        if (isMobile) {
            // On mobile, collapsed = true means hidden (translateX(-100%))
            // So we want to toggle between hidden and visible
            const newState = forceClose ? true : !leftCollapsed;
            setLeftCollapsed(newState);
            // Mobile: opening left closes right
            if (!newState && rightVisible) {
                setRightVisible(false);
            }
        } else {
            setLeftCollapsed(!leftCollapsed);
        }
    };

    const toggleRightSidebar = (forceClose = false) => {
        const newState = forceClose ? false : !rightVisible;
        setRightVisible(newState);
        // Mobile: opening right closes left
        if (isMobile && newState && !leftCollapsed) {
            setLeftCollapsed(true);
        }
    };

    const toggleLeftVisibility = () => {
        setLeftHidden(!leftHidden);
    };

    // Resize handlers
    const handleLeftMouseDown = (e) => {
        e.preventDefault();
        setIsLeftResizing(true);
    };

    const handleRightMouseDown = (e) => {
        e.preventDefault();
        setIsRightResizing(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isLeftResizing) {
                const newWidth = Math.max(200, Math.min(400, e.clientX));
                setLeftWidth(newWidth);
            }
            if (isRightResizing) {
                const newWidth = Math.max(250, Math.min(500, contentWidth - e.clientX));
                setRightWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsLeftResizing(false);
            setIsRightResizing(false);
        };

        if (isLeftResizing || isRightResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isLeftResizing, isRightResizing, contentWidth]);

    const value = {
        // Left sidebar
        leftCollapsed,
        leftWidth,
        leftHidden,
        isLeftResizing,
        toggleLeftSidebar,
        toggleLeftVisibility,
        handleLeftMouseDown,
        
        // Right sidebar
        rightVisible,
        rightWidth,
        isRightResizing,
        toggleRightSidebar,
        handleRightMouseDown,
        
        // Shared
        isMobile,
        contentWidth,
        getContentMargins,
        isAuthenticated,
        
        // Page-specific preferences
        applyLogisticsDefaults,
        applyRegularDefaults,
        savePreferences,
        
        // Legacy support (map to left sidebar)
        collapsed: leftCollapsed,
        width: leftWidth,
        hidden: leftHidden,
        isResizing: isLeftResizing,
        toggleSidebar: toggleLeftSidebar,
        toggleVisibility: toggleLeftVisibility,
        handleMouseDown: handleLeftMouseDown,
    };

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};